/**
 * Vue I18n initialization for GonkaGame
 * Supports 13 languages with lazy loading and RTL
 */

import { createI18n } from 'vue-i18n'
import {
    SUPPORTED_LOCALE_CODES,
    DEFAULT_LOCALE,
    FALLBACK_LOCALE,
    isLocaleSupported,
    isRtlLocale,
} from './config'

// Import default locale synchronously (fallback)
import en from './locales/en.json'
import { seoEn, seoRu } from './seo'
import aiStudioEn from '@/modules/ai-studio/locales/en'
import aiStudioRu from '@/modules/ai-studio/locales/ru'
import portalEn from '@/modules/payment/locales/portal/en.json'
import portalRu from '@/modules/payment/locales/portal/ru.json'
import { nyroMessages } from '@/modules/profile/locales/nyroMessages'
import { entryMessages } from '@/modules/auth/locales/entry'
import { sponsorMessages } from '@/modules/profile/locales/sponsor'
import { emailLinkMessages } from '@/modules/profile/locales/emailLink'
import { tmaEmailMessages } from '@/mini-app/locales/email'

// Cookie name for user language preference
const LOCALE_COOKIE_NAME = 'language'

/**
 * Get cookie value by name
 */
function getCookie(name: string): string | null {
    if (typeof document === 'undefined') return null
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'))
    return match ? decodeURIComponent(match[2]) : null
}

/**
 * Set cookie value with security attributes
 */
function setCookie(name: string, value: string, days = 365): void {
    if (typeof document === 'undefined') return
    const date = new Date()
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000)
    const expires = '; expires=' + date.toUTCString()

    // Improved security attributes for production
    let cookie = name + '=' + (value || '') + expires + '; path=/; SameSite=Lax'
    if (window.location.protocol === 'https:') {
        cookie += '; Secure'
    }
    document.cookie = cookie
}

/**
 * Detect initial locale based on priority:
 * 1. Cookie 'language' (user's manual choice)
 * 2. navigator.language (browser preference)
 * 3. Default locale (en)
 */
function detectLocale(): string {
    // 1. Check Cookie
    const saved = getCookie(LOCALE_COOKIE_NAME)
    if (saved && isLocaleSupported(saved)) {
        return saved
    }

    // 2. Check browser language
    if (typeof navigator !== 'undefined') {
        const browserLang = navigator.language?.split('-')[0]
        if (browserLang && isLocaleSupported(browserLang)) {
            return browserLang
        }

        // Check navigator.languages array
        const languages = navigator.languages || []
        for (const lang of languages) {
            const code = lang.split('-')[0]
            if (isLocaleSupported(code)) {
                return code
            }
        }
    }

    // 3. Fallback to default
    return DEFAULT_LOCALE
}

// Create i18n instance
const initialLocale = detectLocale()
if (typeof document !== 'undefined') {
    document.documentElement.dir = isRtlLocale(initialLocale) ? 'rtl' : 'ltr'
    document.documentElement.lang = initialLocale
}

/**
 * Slavic plural rule (Russian, Ukrainian):
 * 0 = zero/other, 1 = one (1, 21, 31...), 2 = few (2-4, 22-24...), 3 = many (5-20, 25-30...)
 * Vue-i18n pipe format: zero | one | few | many
 */
export function slavicPluralRule(choice: number, choicesLength: number): number {
    if (choice === 0) return 0

    const mod10 = choice % 10
    const mod100 = choice % 100

    // one: 1, 21, 31, 41 ... (but not 11)
    if (mod10 === 1 && mod100 !== 11) {
        return choicesLength < 4 ? (choicesLength < 3 ? 1 : 2) : 1
    }

    // few: 2-4, 22-24, 32-34 ... (but not 12-14)
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) {
        return choicesLength < 4 ? (choicesLength < 3 ? 1 : 2) : 2
    }

    // many: 0, 5-20, 25-30 ...
    return choicesLength < 4 ? (choicesLength < 3 ? 1 : 2) : 3
}

export const i18n = createI18n({
    legacy: false, // Use Composition API
    locale: initialLocale,
    fallbackLocale: FALLBACK_LOCALE,
    messages: {
        en: { ...en, aiStudio: aiStudioEn, seo: seoEn, portal: portalEn, nyro: nyroMessages.en, entry: entryMessages.en, sponsorRequired: sponsorMessages.en, emailLink: emailLinkMessages.en, tmaEmail: tmaEmailMessages.en },
        ru: { seo: seoRu, portal: portalRu, nyro: nyroMessages.ru, entry: entryMessages.ru, sponsorRequired: sponsorMessages.ru, emailLink: emailLinkMessages.ru, tmaEmail: tmaEmailMessages.ru },
    },
    pluralRules: {
        ru: slavicPluralRule,
        ua: slavicPluralRule,
        pl: slavicPluralRule,
        cz: slavicPluralRule,
    },
    missingWarn: import.meta.env.DEV,
    fallbackWarn: import.meta.env.DEV,
})

// Cache for loaded locales
const loadedLocales = new Set<string>(['en'])

// Use import.meta.glob for more robust dynamic loading in production
const localesMap = import.meta.glob('./locales/*.json')

/**
 * Lazy load a locale and set it as active
 */
export async function loadLocale(locale: string): Promise<void> {
    if (!isLocaleSupported(locale)) {
        console.warn(`[i18n] Locale "${locale}" is not supported`)
        return
    }

    // Load locale if not already loaded
    if (!loadedLocales.has(locale)) {
        const filePath = `./locales/${locale}.json`
        const loadFn = localesMap[filePath]

        if (!loadFn) {
            console.error(`[i18n] Localization file not found: ${filePath}`)
            return
        }

        try {
            const messages = await loadFn() as { default: any }
            i18n.global.setLocaleMessage(locale, {
                ...messages.default,
                nyro: nyroMessages[locale] ?? nyroMessages.en,
                entry: locale === 'ru' ? entryMessages.ru : entryMessages.en,
                sponsorRequired: locale === 'ru' ? sponsorMessages.ru : sponsorMessages.en,
                emailLink: locale === 'ru' ? emailLinkMessages.ru : emailLinkMessages.en,
                tmaEmail: locale === 'ru' ? tmaEmailMessages.ru : tmaEmailMessages.en,
                ...(locale === 'ru' ? { aiStudio: aiStudioRu, seo: seoRu, portal: portalRu } : {}),
            })
            loadedLocales.add(locale)
        } catch (error) {
            console.error(`[i18n] Failed to load locale "${locale}"`, error)
            return
        }
    }

    // Set as active locale
    ; (i18n.global.locale as unknown as { value: string }).value = locale

    // Persist to Cookie
    setCookie(LOCALE_COOKIE_NAME, locale)

    // Update HTML dir attribute for RTL languages
    if (typeof document !== 'undefined') {
        document.documentElement.dir = isRtlLocale(locale) ? 'rtl' : 'ltr'
        document.documentElement.lang = locale
    }
}

/**
 * Get current locale
 */
export function getCurrentLocale(): string {
    return i18n.global.locale.value as string
}

/**
 * Get list of available locale codes
 */
export function getAvailableLocales(): readonly string[] {
    return SUPPORTED_LOCALE_CODES
}

export default i18n
